import AttReportBtn from "components/Pdfs/Attendance/AttByMonthSIngleUser/AttReportBtn";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import {
  calculateTimeDifference,
  convertToDecimalHours,
  getCurTimeOfDeviceTimeZone,
  getDatesArrByMonth,
  getDayNameByDate,
  getTotalHours,
  getTotalLateTime,
  getTotalOvertime,
} from "utils/attendanceUtility";
import {
  getNightUserTotalHours,
  getNightUserTotalLateTime,
  getNightUserTotalOvertime,
} from "utils/NightUserAttendanceUtility";

export default function GenerateAttendance({
  deviceId,
  userData,
  selectedMonth: _selectedMonth = null,
}) {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  /*   All States
   ********************************************* */
  const [showModal, setShowModal] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(_selectedMonth || "");
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const [selectedDevice, setSelectedDevice] = useState(null);

  // States for user navigation
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [usersList, setUsersList] = useState([]);

  /*   All Functions
   ********************************************* */
  const loadingOn = () => {
    setLoading(true);
  };

  const loadingOff = () => {
    setLoading(false);
  };

  const handleSelectMonth = (event) => {
    setSelectedReport(null);
    setSelectedMonth(event.target.value);
  };

  const getUsersDataByDeviceId = async (id) => {
    loadingOn();
    try {
      let filterBy = searchParams.get("filterBy") || "active";
      const { data } = await API.post(
        "/device-users-list/" + id,
        { filterBy },
        {
          headers: authHeader(),
        },
      );

      if (data?.success) {
        // Update usersList and current user index if usersList is available
        if (data?.data && data?.data.length > 0) {
          setUsersList(data.data);
          const currentIndex = data.data.findIndex(
            (user) => user.uid === parseInt(selectedUser?.userId, 10),
          );
          setCurrentUserIndex(currentIndex !== -1 ? currentIndex : 0);
          setSelectedReport(null);
          setSelectedUser(data.data[currentIndex !== -1 ? currentIndex : 0]);
        }
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      loadingOff();
    }
  };

  // Get Device Id
  const GetDeviceById = async () => {
    try {
      if (!deviceId) {
        return;
      }
      loadingOn();
      const { data } = await API.get("/get-attendance-device/" + deviceId, {
        headers: authHeader(),
      });
      if (data?.success) {
        setSelectedDevice(data.data);
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  // Make Api call to get logs by selected Month
  const getLogsByDateForSelectedUser = async ({ startDate, endDate, year }) => {
    try {
      loadingOn();

      const { data } = await API.post(
        "/get-user-logs/" + selectedUser?.userId,
        {
          deviceId,
          startDate: startDate,
          endDate: endDate,
          year,
        },
        {
          headers: authHeader(),
        },
      );

      loadingOff();
      return data;
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  /* 
  1. Sets Start and End of month 
  2. Makes Api call based on start-end month
  3. Checks if User is Night Office Member and run function accordingly
   */

  const GenerateReport = useCallback(
    async (e) => {
      setSelectedReport(null);

      if (!selectedMonth) {
        dispatch(
          setNotification({
            message: "Please Select Month",
            type: "error",
          }),
        );
        return;
      }

      if (
        !selectedUser?.weeklySchedule ||
        Object.keys(JSON.parse(selectedUser?.weeklySchedule)).length === 0
      ) {
        dispatch(
          setNotification({
            message: "Please Set Weekly Schedule",
            type: "error",
          }),
        );
        return;
      }

      if (!selectedDevice?.id || !selectedDevice?.timezone) {
        dispatch(
          setNotification({
            message: "Please Select Device with timezone",
            type: "error",
          }),
        );
        setLoading(false);
        return;
      }

      const pakistanStartDate = new Date(
        selectedMonth.toLocaleString("en-US", {
          timeZone: selectedDevice?.timezone,
        }),
      );
      // set hours to 0, minutes to 0, seconds to 0
      pakistanStartDate.setUTCHours(0, 0, 0, 0);

      const pakistanEndDate = new Date(pakistanStartDate);
      // Set end date to the last day of the month
      const endOfMonth = new Date(
        pakistanEndDate.getUTCFullYear(),
        pakistanEndDate.getUTCMonth() + 1,
        2,
      );
      endOfMonth.setUTCHours(23, 59, 59, 999);

      // Increment 2 day if selectedUser?.isNightOffice is true
      if (selectedUser?.isNightOffice) {
        endOfMonth.setDate(endOfMonth.getDate() + 1);
      }

      // get year from startDate
      const year = pakistanStartDate.getUTCFullYear();

      if (!year) {
        dispatch(
          setNotification({
            message: "Invalid Year",
            type: "error",
          }),
        );
        return;
      }

      // console.log("Pakistan Start Date:", pakistanStartDate.toISOString());
      // console.log("Pakistan End Date:", endOfMonth.toISOString());

      // Make Api call based on start-end month
      const data = await getLogsByDateForSelectedUser({
        startDate: pakistanStartDate.toISOString(),
        endDate: endOfMonth.toISOString(),
        year,
      });

      if (data?.success) {
        if (userData && userData?.isNightOffice) {
          processNightOfficeLogsToRecords(data.data, data?.holidaysList);
          // getAttendanceReport(data.data, data?.holidaysList);
        } else {
          getAttendanceReport(data.data, data?.holidaysList);
        }
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          }),
        );
      }
    },
    [selectedMonth, selectedUser, dispatch],
  );

  // Navigate to the next user in the list
  const goToNextUser = useCallback(() => {
    if (usersList.length === 0 || currentUserIndex >= usersList.length - 1)
      return;

    const nextIndex = currentUserIndex + 1;
    const nextUser = usersList[nextIndex];

    setSelectedReport((prev) => null);
    setCurrentUserIndex(nextIndex);
    setSelectedUser(nextUser);

    // Update URL and fetch data for the new user

    // Update params directly for the next API call
  }, [usersList, currentUserIndex, deviceId]);

  // Navigate to the previous user in the list
  const goToPrevUser = useCallback(() => {
    if (usersList.length === 0 || currentUserIndex <= 0) return;

    const prevIndex = currentUserIndex - 1;
    const prevUser = usersList[prevIndex];

    setSelectedReport((prev) => null);
    setCurrentUserIndex(prevIndex);
    setSelectedUser(prevUser);

    // Update URL and fetch data for the new user

    // Update params directly for the next API call
  }, [usersList, currentUserIndex, deviceId]);

  useEffect(() => {
    if (userData && usersList.length > 0) {
      // get curr index
      const currentIndex = usersList.findIndex(
        (user) => user.uid === parseInt(userData?.userId, 10),
      );
      setCurrentUserIndex(currentIndex !== -1 ? currentIndex : 0);
      setSelectedUser(usersList[currentIndex !== -1 ? currentIndex : 0]);
    }
  }, [usersList, userData]);

  /*   Day Office
   **************** */
  /*   Generate Attendance Report for Day/Night
   **************** */
  const getAttendanceReport = (attendanceRecords, holidaysList) => {
    if (!holidaysList) {
      return dispatch(
        setNotification({
          message: "Holidays getting failed",
          type: "error",
        }),
      );
    }

    if (!attendanceRecords || attendanceRecords.length === 0) {
      attendanceRecords = [];
    }

    setSelectedReport((prev) => null);

    // record month = 2025-07

    let recordMonthYear = selectedMonth?.split("-");
    let currentMonth = recordMonthYear[1];
    let currentYear = recordMonthYear[0];

    // console.log("selectedMonth", selectedMonth);
    // console.log("recordMonth", currentMonth);

    // Get Month-Year of selected month (eg. 08-2024) Used for getting array of All days of month
    let monthYear = `${currentMonth}-${currentYear}`;

    const recordsByDay = {};
    /* 
    Looping Over Attendance records arr received by database
    1.Getting Date and Time from timestamp of database and custom formatting it.
    2.Appending to records by day: { day : records-of-that-day } format.
     */

    attendanceRecords.forEach((record) => {
      const date = new Date(record.recordTime);

      // Convert to proper UTC time format without using local time
      const hours = date.getUTCHours().toString().padStart(2, "0");
      const minutes = date.getUTCMinutes().toString().padStart(2, "0");
      const seconds = date.getUTCSeconds().toString().padStart(2, "0");

      const formattedTime = `${hours}:${minutes}:${seconds}`;

      // Get date in UTC for consistent date handling
      const formattedDate = `${
        date.getUTCMonth() + 1
      }-${date.getUTCDate()}-${date.getUTCFullYear()}`;

      // Properly determine day of week based on UTC date
      recordsByDay[formattedDate] = (recordsByDay[formattedDate] || []).concat({
        ...record,
        formattedTime,
        formattedDate,
        dayOfWeek: getDayNameByDate(date),
      });
    });

    /* 
    Since day can have multiple records. We need only In-Time and OutTime records of each Date.
     */
    const parsedRecords = Object.keys(recordsByDay).reduce((prev, key) => {
      const records = recordsByDay[key];
      let nextDate = new Date(key);
      nextDate.setDate(nextDate.getDate() + 1);

      let formattedNextDate = `${
        nextDate.getMonth() + 1
      }-${nextDate.getDate()}-${nextDate.getFullYear()}`;

      prev[key] = getProcessedDayOfficeData(records);
      return prev;
    }, {});

    /* 
    Only get those records which are of selected month
    eg. 08-01-2024 to 08-31-2024 if there are 31 days. 
    */

    const filteredRecords = Object.entries(parsedRecords)
      .filter(([key]) => {
        const [month, day, year] = key.split("-").map(Number);
        // Fix: months are zero-based in JS Date
        const date = new Date(Date.UTC(year, month - 1, day));
        return (
          date.getUTCMonth() + 1 === parseInt(currentMonth) &&
          date.getUTCFullYear() === parseInt(currentYear)
        );
      })
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {});

    // console.log("parsedRecords", parsedRecords);
    // console.log("filteredRecords", filteredRecords);

    const weeklySchedule = JSON.parse(selectedUser?.weeklySchedule);

    /**
     * Check for holidays
     * holidayList  = {
          "name": "Independence Day ",
          "date": "2024-08-14",
          "id": "b677dbe2-3cf3-4c9d-acfd-e4969b29c968"
        }
         
      Add Holidays to list of attendance.
      Rules:
        Overwrite user entry if exists in holiday.
          ↘️ Calculate expected hours and add as overtime & totalTime.

      return object for each holiday = {
      inTime, // same as userInTime
      outTime, // same as userOutTime
      formattedDate,
      dayOfWeek,
      
      dayNameOfWeek,
      userInTime,
      userOutTime,
      totalExpectedHours: totalExpectedHoursInDecimal,

      totalLateTime: 0.00,  
      totalHours: , // total expected hours
      overTime: , // total expected hours
    }

      Steps: 
      Get holidays for selectedMonth.
      return object for each holiday.
     */

    const filteredHolidays =
      !holidaysList ?
        []
      : holidaysList.filter((holiday) => {
          const [year, month, day] = holiday.date
            .split("T")[0]
            .split("-")
            .map(Number);

          const holidayDate = new Date(Date.UTC(year, month - 1, day));
          // Fix: compare getUTCMonth()+1 to currentMonth
          return (
            holidayDate.getUTCMonth() + 1 === parseInt(currentMonth) &&
            holidayDate.getUTCFullYear() === parseInt(currentYear)
          );
        });

    filteredHolidays.forEach((holiday) => {
      const holidayDate = holiday.date;
      const [year, month, day] = holidayDate
        .split("T")[0]
        .split("-")
        .map(Number);
      const dateObj = new Date(year, month - 1, day);
      const dayName = getDayNameByDate(dateObj);
      const daySchedule = weeklySchedule[dayName];
      let totalExpectedHoursInDecimal = 0;
      if (daySchedule?.working) {
        const totalExpectedHours = calculateTimeDifference(
          daySchedule.outTime,
          daySchedule.inTime,
        );
        totalExpectedHoursInDecimal = parseFloat(
          convertToDecimalHours(totalExpectedHours).toFixed(2),
        );
      }

      let correctDateFormat = `${month}-${day}-${year}`;

      // shouldCalculateLateTime
      // shouldCalculateOvertime

      /*   Check Overtime
       **************** */
      let overTime = 0.0;
      if (
        filteredRecords[correctDateFormat]?.shouldCalculateOvertime === false
      ) {
        overTime = 0.0;
      } else {
        overTime = filteredRecords[correctDateFormat]?.totalHours || 0.0;
      }

      /*   Check Late Time
       **************** */
      let totalLateTime = 0.0;
      if (
        filteredRecords[correctDateFormat]?.shouldCalculateLateTime === false
      ) {
        totalLateTime = 0.0;
      } else {
        totalLateTime =
          filteredRecords[correctDateFormat]?.totalLateTime || 0.0;
      }

      // console.log(correctDateFormat, filteredRecords[correctDateFormat]);
      filteredRecords[correctDateFormat] = {
        ...filteredRecords[correctDateFormat],
        type: "Holiday",
        message: "Holiday",
        totalLateTime: 0.0,
        overTime: overTime,
      };
    });

    /*   Summary Section
     ********************************************* */

    /**
     * calculate totalLateTime totalOverTime totalWorkingHours
     * From filteredRecords
     **/
    const summaryData = Object.values(filteredRecords).reduce(
      (prev, curr) => {
        if (Object.keys(curr).length === 0) {
          return prev;
        }

        // Add totalLateTime totalOverTime totalWorkingHours
        prev.totalLateTime =
          curr.totalLateTime ?
            parseFloat((prev.totalLateTime + curr.totalLateTime).toFixed(2))
          : prev.totalLateTime;

        prev.totalOverTime =
          curr.overTime ?
            parseFloat((prev.totalOverTime + curr.overTime).toFixed(2))
          : prev.totalOverTime;
        prev.totalWorkingHours =
          curr.totalHours ?
            parseFloat((prev.totalWorkingHours + curr.totalHours).toFixed(2))
          : prev.totalWorkingHours;

        return prev;
      },
      {
        totalLateTime: 0.0,
        totalOverTime: 0.0,
        totalWorkingHours: 0.0,
      },
    );

    let parsedSummaryData = {
      totalLateTime: summaryData.totalLateTime,
      totalOverTime: summaryData.totalOverTime,
      totalWorkingHours: summaryData.totalWorkingHours,
    };

    /** ===== Get Absent Days and Holidays =====
     *
     * Goes through all months date.
     * Gets daySchedule of each day.
     * Checks if its working day and are no records then marks them as absent
     * If working is false then marks them as holidays
     */

    // Getting All dates of month
    let allMonthDates = getDatesArrByMonth(monthYear);
    let absentDaysCount = 0;

    allMonthDates.forEach((date) => {
      // Get Day name for the current day
      let dateObj = new Date(date);
      let dayName = getDayNameByDate(dateObj);

      // get weeklySchedule Object of current day
      let daySchedule = weeklySchedule[dayName];

      // Check record for that day is marked as holiday then return
      if (filteredRecords[date]?.type === "Holiday") {
        return;
      }

      /**
       * if daySchedule?.working === false then add message property that day as holiday
       * if daySchedule?.working === true and there are no records for that day then add message property that day as absent
       */
      if (!filteredRecords[date] && daySchedule?.working === false) {
        filteredRecords[date] = { message: "Holiday" };
      } else if (
        (!filteredRecords[date] && daySchedule?.working === true) ||
        filteredRecords[date]?.message === "absent"
      ) {
        filteredRecords[date] = { message: "Absent" };
        absentDaysCount++;
      }
    });

    parsedSummaryData.absentDaysCount = absentDaysCount;

    setSelectedReport({
      attendanceRecords: filteredRecords,
      userData: selectedUser,
      monthYear,
      absentDaysCount,
      summaryData: parsedSummaryData,
    });

    console.log("filteredRecords", filteredRecords);

    return {
      attendanceRecords: filteredRecords,
      userData: selectedUser,
      monthYear,
      absentDaysCount,
      summaryData: parsedSummaryData,
    };
  };

  /*   Main Function for Day Office
   **************** */
  //  Calculate In-out total-hours total overtime etc.
  const getProcessedDayOfficeData = (arr) => {
    const sortedArr = arr.sort(
      (a, b) => new Date(a.recordTime) - new Date(b.recordTime),
    );

    let userRules = JSON.parse(selectedUser?.rules);

    /* 
    userData = {
    "id": 3,
    "companyId": 3,
    "userId": 3,
    "deviceId": 1,
    "name": "TARIQ",
    "password": "",
    "role": "14",
    "cardno": "4634703",
    "userName": "Tariq Tahir",
    "badgeNo": "007",
    "relaxationMinutes": 15,
    "departmentId": null,
    "weeklySchedule": "{\"Monday\":{\"inTime\":\"10:00\",\"outTime\":\"23:00\",\"working\":true},\"Tuesday\":{\"inTime\":\"10:00\",\"outTime\":\"23:00\",\"working\":true},\"Wednesday\":{\"inTime\":\"10:00\",\"outTime\":\"23:00\",\"working\":true},\"Thursday\":{\"inTime\":\"10:00\",\"outTime\":\"23:00\",\"working\":true},\"Friday\":{\"inTime\":\"10:00\",\"outTime\":\"23:00\",\"working\":true},\"Saturday\":{\"inTime\":\"\",\"outTime\":\"\",\"working\":false},\"Sunday\":{\"inTime\":\"\",\"outTime\":\"\",\"working\":false}}",
    rules:"{"overTime":{"name":"Overtime Calculation","description":"Calculate overtime after expected hours + overtimeAfterMinutes","isChecked":true},"lateTime":{"name":"Late Time Calculation","description":"Only calculate late time when inTime is greater than inTimeWithRelaxationFormatted. Count late time from userInTime when inTime is less than inTimeWithRelaxationFormatted","isChecked":true}}"
    "isNightOffice": false,
    "groupId": null,
    "createdAt": "2024-10-24T09:35:58.000Z",
    "updatedAt": "2024-11-13T07:42:02.000Z"
}
     */

    /**
     * After sorting records by Day
     * We only need 2 entries first and last entry.
     * First entry will always be inTime
     * Last entry will always be outTime
     * We also have user defined UserInTime and UserOutTime in UserData
     * We get overtime and late time comparing inTime-outTime with UserInTime-UserOutTime
     */
    const firstEntry = sortedArr.slice(0, 1);
    const lastEntry = sortedArr.slice(-1);

    const date = new Date(firstEntry[0].recordTime);
    let dayOfWeek = date.getDate();

    let formattedDate = firstEntry[0]?.formattedDate;
    let dayNameOfWeek = getDayNameByDate(date);

    /**
     * Get dayOfWeek from firstEntry (eg. Monday, Tuesday,.....)
     * Get inTime and outTime from selectedUser?.weeklySchedule[dayOfWeek]
     * Also check if selectedUser?.weeklySchedule[dayOfWeek].working === true
     */

    const weeklySchedule = JSON.parse(selectedUser?.weeklySchedule);
    const daySchedule = weeklySchedule[dayNameOfWeek];

    let userInTime = daySchedule.inTime;
    let userOutTime = daySchedule.outTime;

    let inTime = firstEntry[0]?.formattedTime;
    let outTime = lastEntry[0]?.formattedTime;

    let relaxationMinutes = selectedUser?.relaxationMinutes;
    let overTimeAfterMinutes = selectedUser?.overtimeAfterMinutes;

    // console.log("date", formattedDate);

    /* Total Expected Hours
     **************** */
    let totalExpectedHoursInDecimal =
      getTotalHours(userInTime, userOutTime) || 0.0;

    /* Total Late Time
     **************** */
    let totalLateTimeValue =
      getTotalLateTime(
        inTime,
        outTime,
        userInTime,
        userOutTime,
        relaxationMinutes,
        userRules,
        selectedDevice,
      ) || 0.0;

    // check if holiday

    /*   Total Hours Worked
     **************** */
    let totalHoursValue = getTotalHours(inTime, outTime) || 0.0;

    /*   Total OverTime
     **************** */
    let overTimeValue =
      getTotalOvertime(
        inTime,
        outTime,
        userInTime,
        userOutTime,
        overTimeAfterMinutes,
        userRules,
        selectedDevice,
      ) || 0.0;
    // if (formattedDate === "11-2-2025" || formattedDate === "11-3-2025") {
    //   console.log(formattedDate, dayOfWeek);
    //   console.log("overTimeValue", overTimeValue);
    //   console.log("-----------------");
    // }

    let shouldCalculateOvertime =
      firstEntry[0].calculateOverTime === false ? false : true;

    // set overtime to 0 if calculateOvertime is false
    if (shouldCalculateOvertime === false) {
      overTimeValue = 0.0;
    }

    let shouldCalculateLateTime =
      firstEntry[0].calculateLateTime === false ? false : true;

    // set lateTime to 0 if calculateLateTime is false
    if (shouldCalculateLateTime === false) {
      totalLateTimeValue = 0.0;
    }

    // throw new Error("Not Implemented");

    return {
      inTime,
      outTime,
      shouldCalculateOvertime,
      shouldCalculateLateTime,
      formattedDate,
      dayOfWeek,
      inRecord: firstEntry[0].recordTime,
      outRecord: lastEntry[0].recordTime,
      dayNameOfWeek,
      userInTime,
      userOutTime,
      totalExpectedHours: totalExpectedHoursInDecimal,

      totalLateTime: inTime === outTime ? 0.0 : parseFloat(totalLateTimeValue),
      totalHours: inTime === outTime ? 0.0 : parseFloat(totalHoursValue),
      overTime: inTime === outTime ? 0.0 : parseFloat(overTimeValue),
    };
  };

  /*   Night Office
   **************** */
  const getDaysInMonth = useCallback(() => {
    const [year, month] = selectedMonth.split("-");
    return new Date(year, month, 0).getDate();
  }, [selectedMonth]);

  function buildNightShiftSessions(logs) {
    // Sort logs by original timestamp (UTC safe)
    logs.sort((a, b) => new Date(a.recordTime) - new Date(b.recordTime));

    // Step 1: Group logs by date and separate IN/OUT records
    const dateGroups = {};

    for (let log of logs) {
      const dt = new Date(log.recordTime);
      const hour = dt.getUTCHours();
      const rawDate = log.recordTime.slice(0, 10);

      // Initialize date group if not exists
      if (!dateGroups[rawDate]) {
        dateGroups[rawDate] = {
          inRecords: [], // hour >= 12 (IN times for this date)
          outRecords: [], // hour < 12 (OUT times, belong to PREVIOUS date)
        };
      }

      if (hour >= 12) {
        // IN record for this date
        dateGroups[rawDate].inRecords.push(log);
      } else {
        // OUT record (belongs to previous date)
        dateGroups[rawDate].outRecords.push(log);
      }
    }

    // Step 2: Build sessions by assigning IN to same date, OUT to previous date
    const sessionsMap = {}; // key: date, value: session object

    for (const [date, group] of Object.entries(dateGroups)) {
      // Process IN records (earliest IN for this date)
      if (group.inRecords.length > 0) {
        // Sort by recordTime ascending, get earliest
        group.inRecords.sort(
          (a, b) => new Date(a.recordTime) - new Date(b.recordTime),
        );
        const earliestIn = group.inRecords[0];

        if (!sessionsMap[date]) {
          sessionsMap[date] = {
            date: date,
            in: null,
            inTimeRecordId: null,
            inLog: null,
            inRecords: [],
            out: null,
            outTimeRecordId: null,
            outLog: null,
            outRecords: [],
          };
        }

        sessionsMap[date].in = earliestIn.recordTime;
        sessionsMap[date].inTimeRecordId = earliestIn.id;
        sessionsMap[date].inLog = earliestIn;
        sessionsMap[date].inRecords = group.inRecords;
      }

      // Process OUT records (latest OUT belongs to PREVIOUS date)
      if (group.outRecords.length > 0) {
        // Sort by recordTime descending, get latest
        group.outRecords.sort(
          (a, b) => new Date(b.recordTime) - new Date(a.recordTime),
        );
        const latestOut = group.outRecords[0];

        // Calculate previous date
        const prevDateObj = new Date(date + "T00:00:00Z");
        prevDateObj.setUTCDate(prevDateObj.getUTCDate() - 1);
        const prevDate = prevDateObj.toISOString().slice(0, 10);

        if (!sessionsMap[prevDate]) {
          sessionsMap[prevDate] = {
            date: prevDate,
            in: null,
            inTimeRecordId: null,
            inLog: null,
            inRecords: [],
            out: null,
            outTimeRecordId: null,
            outLog: null,
            outRecords: [],
          };
        }

        sessionsMap[prevDate].out = latestOut.recordTime;
        sessionsMap[prevDate].outTimeRecordId = latestOut.id;
        sessionsMap[prevDate].outLog = latestOut;
        sessionsMap[prevDate].outRecords = group.outRecords;
      }
    }

    // Step 3: Convert map to sorted array
    const sessions = Object.values(sessionsMap).sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    );

    // console.log("logs", logs);
    // console.log("sessions", sessions);

    return sessions;
  }

  // Process logs to show only check-in and check-out for each date
  const processNightOfficeLogsToRecords = (logs, holidayList) => {
    const daysInMonth = getDaysInMonth();
    const [year, month] = selectedMonth.split("-");
    const records = {};

    // Prepare a set of holiday dates in both ISO (YYYY-MM-DD) and M-D-YYYY to avoid tz/format drift across servers
    const holidayDatesSet = new Set(
      (holidayList || []).flatMap((h) => {
        const isoDate = h.date.split("T")[0];
        const [y, m, d] = isoDate.split("-");
        const mdyDate = `${parseInt(m)}-${parseInt(d)}-${y}`;
        return [isoDate, mdyDate];
      }),
    );

    const isHolidayDate = (isoDate, mdyDate) =>
      holidayDatesSet.has(isoDate) || holidayDatesSet.has(mdyDate);

    let recordsSessions = buildNightShiftSessions(logs);
    // console.log("recordsSessions", recordsSessions);

    /*   Loop Process Start
     **************** */
    /**
     * REQUIRED FORMAT
     * {
          ✅"inTime": "19:50:00",
          ✅"outTime": "19:50:00",
          ✅"shouldCalculateOvertime": true,
          ✅"shouldCalculateLateTime": true,
          ✅"formattedDate": "12-1-2025",
          ✅"dayNameOfWeek": "Tuesday",
          ✅"dayOfWeek": 2,
          ✅"inRecord": "2025-12-01T19:50:00.000Z",
          ✅"outRecord": "2025-12-01T19:50:00.000Z",
          "userInTime": "19:00",
          "userOutTime": "01:00",
          "totalExpectedHours": 18.02,
          "totalLateTime": 0,
          "totalHours": 0,
          "overTime": 0
        }
      *************************    
      * WHAT IS CURRENTLY BUILT
        {
        "date": "2025-12-01",
        "inTime": "19:50",
        "inTimeRecordId": 15050,
        "inLog": {
            "id": 15050,
            "companyId": 3,
            "deviceId": 6,
            "userId": 2,
            "_recordTime": null,
            "recordTime": "2025-12-01T19:50:00.000Z",
            "isDeviceRecord": false,
            "calculateOverTime": true,
            "calculateLateTime": true,
            "createdAt": "2025-12-08T10:15:23.000Z",
            "updatedAt": "2025-12-08T17:08:52.000Z"
        },
        "inLogs": [],
        "dayLogs": [],
        "outTime": "01:15",
        "outTimeRecordId": 15051,
        "outLog": {
            "id": 15051,
            "companyId": 3,
            "deviceId": 6,
            "userId": 2,
            "_recordTime": null,
            "recordTime": "2025-12-02T01:15:00.000Z",
            "isDeviceRecord": false,
            "calculateOverTime": true,
            "calculateLateTime": true,
            "createdAt": "2025-12-08T10:15:23.000Z",
            "updatedAt": "2025-12-08T10:15:23.000Z"
        },
        "outLogs": [],
        "isHoliday": false,
        "dayName": "Monday",
        "calculateOverTime": true,
        "calculateLateTime": true
    }
           */
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${year}-${month}-${String(day).padStart(2, "0")}`;

      let sessionForTheDay = recordsSessions.find(
        (session) => session.date === dateString,
      );
      // Use parseInt to remove leading zeros to match holidayDatesSet format
      let formattedDate = `${parseInt(month)}-${day}-${year}`;
      if (!selectedUser?.weeklySchedule) {
        return "Weekly schedule not set";
      }

      let weeklySchedule = JSON.parse(selectedUser?.weeklySchedule || {});
      let rules = JSON.parse(selectedUser?.rules || "{}");

      let record = {};

      if (sessionForTheDay === undefined) {
        // get dayName
        let dayNameOfWeek = getDayNameByDate(
          new Date(dateString + "T00:00:00Z"),
        );
        let dayOfWeek = new Date(dateString).getUTCDate();

        record = {
          date: formattedDate,
          message: "Absent",
          type: "Absent",
          inTime: "",
          outTime: "",
          overTime: 0.0,
          totalHours: 0.0,
          totalLateTime: 0.0,
          userInTime: weeklySchedule[dayNameOfWeek]?.inTime || "",
          userOutTime: weeklySchedule[dayNameOfWeek]?.outTime || "",
          inTimeRecordId: null,
          outTimeRecordId: null,
          calculateOverTime: false,
          calculateLateTime: false,
          dayLogs: [],
          isHoliday: isHolidayDate(dateString, formattedDate),
          formattedDate: formattedDate,
          dayOfWeek: dayOfWeek,
          dayNameOfWeek: dayNameOfWeek,
        };
        records[formattedDate] = record;
        continue;
      }

      let date = new Date(dateString + "T00:00:00Z");
      let dayNumberOfWeek = date.getUTCDay();

      // if invalid date in inRecords

      // if (isNaN(date.getTime())) {
      //   date = "1970-01-01T00:00:00Z";
      // }
      // console.log(
      //   "checking for invalid date",
      //   sessionForTheDay?.inRecords[0]?.recordTime
      // );

      let dayNameOfWeek = getDayNameByDate(date);

      record = {
        date: formattedDate,
        formattedDate,
        dayOfWeek: dayNumberOfWeek,
        dayNameOfWeek: dayNameOfWeek,
        dayLogs: [],
      };

      /*   Need All Date manipulations in UTC En-US format
       **************** */
      // set inTime and outTime from session
      if (sessionForTheDay.in) {
        // record.inTime = sessionForTheDay.in;
        const inTimeDate = new Date(sessionForTheDay.in);
        const inHours = String(inTimeDate.getUTCHours()).padStart(2, "0");
        const inMinutes = String(inTimeDate.getUTCMinutes()).padStart(2, "0");

        record.inTime = `${inHours}:${inMinutes}`;
        record.inTimeRecordId = sessionForTheDay.inTimeRecordId;
        record.inLog = sessionForTheDay.inLog;
        const inRecords = sessionForTheDay.inRecords || [];
        record.inLogs = inRecords;
        record.dayLogs = [...inRecords];
      }
      if (sessionForTheDay.out) {
        record.outTime = sessionForTheDay.out;
        const outTimeDate = new Date(sessionForTheDay.out);
        const outHours = String(outTimeDate.getUTCHours()).padStart(2, "0");
        const outMinutes = String(outTimeDate.getUTCMinutes()).padStart(2, "0");
        record.outTime = `${outHours}:${outMinutes}`;
        record.outTimeRecordId = sessionForTheDay.outTimeRecordId;
        record.outLog = sessionForTheDay.outLog;
        const outRecords = sessionForTheDay.outRecords || [];
        record.outLogs = outRecords;
        record.dayLogs = [...(record.dayLogs || []), ...outRecords];
      }

      // add day name
      const dayName = new Date(dateString).toLocaleDateString("en-US", {
        weekday: "long",
        timeZone: "UTC",
      });
      record.dayName = dayName;

      // Ensure dayLogs is always an array
      const dayLogs = record.dayLogs || [];
      record.dayLogs = dayLogs;

      // Loop through logs to check if any logs for day has calculateOverTime or calculateLateTime
      record.calculateOverTime = dayLogs.some((log) => log.calculateOverTime);

      record.calculateLateTime = dayLogs.some((log) => log.calculateLateTime);

      /******************************************************************** */

      /*   Holiday & Total Hours, OverTime, LateTime
       **************** */

      // check for holiday (using formattedDate which is in M-D-YYYY format)
      let isHoliday = isHolidayDate(dateString, formattedDate);

      // Store isHoliday flag in the record for later use
      record.isHoliday = isHoliday;

      let inTime = sessionForTheDay.in ? record.inTime : false;
      let outTime = sessionForTheDay.out ? record.outTime : false;
      // Get Total Hours

      if (!inTime || !outTime) {
        record.totalHours = 0.0;
        record.totalLateTime = 0.0;
        record.overTime = 0.0;
      } else {
        /********************************************* */
        /*   Total Hours
         **************** */
        let totalHours = getNightUserTotalHours(inTime, outTime);
        record.totalHours = parseFloat(totalHours);

        /*   Late Time
         **************** */
        const lateTime = getNightUserTotalLateTime(
          inTime,
          outTime,
          weeklySchedule[dayNameOfWeek]?.inTime,
          weeklySchedule[dayNameOfWeek]?.outTime,
          selectedUser?.relaxationMinutes,
          rules,
          selectedDevice,
        );
        record.totalLateTime = parseFloat(lateTime);
        /*   OverTime
         **************** */
        const overTime = getNightUserTotalOvertime(
          inTime,
          outTime,
          weeklySchedule[dayNameOfWeek]?.inTime,
          weeklySchedule[dayNameOfWeek]?.outTime,
          selectedUser?.overtimeAfterMinutes,
          rules,
          selectedDevice,
        );
        record.overTime = parseFloat(overTime);
      }

      if (isHoliday) {
        // reset totalLateTime to 0
        record.totalLateTime = 0.0;

        // reset total hours to expected hours
        let expectedHours = getNightUserTotalHours(
          weeklySchedule[dayNameOfWeek]?.inTime,
          weeklySchedule[dayNameOfWeek]?.outTime,
        );
        record.overTime = parseFloat(expectedHours);
        record.totalHours = parseFloat(expectedHours);
        record.type = "Holiday";
        record.message = "Holiday";
      }

      records[formattedDate] = record;
    }

    // Post-process: Convert absent days that are holidays
    Object.keys(records).forEach((dateKey) => {
      const record = records[dateKey];

      // If record is marked as holiday but still shows as "Absent", convert it
      if (record.isHoliday && record.message === "Absent") {
        const dayNameOfWeek = record.dayNameOfWeek;
        const weeklySchedule = JSON.parse(selectedUser?.weeklySchedule || "{}");

        // Calculate expected hours for the holiday
        let expectedHours = getNightUserTotalHours(
          weeklySchedule[dayNameOfWeek]?.inTime,
          weeklySchedule[dayNameOfWeek]?.outTime,
        );

        // Mark as holiday and set expected hours as overtime
        record.type = "Holiday";
        record.message = "Holiday";
        record.totalLateTime = 0.0;
        record.overTime = parseFloat(expectedHours) || 0.0;
        record.totalHours = parseFloat(expectedHours) || 0.0;
      }
    });

    let recordMonthYear = selectedMonth?.split("-");
    let currentMonth = recordMonthYear[1];
    let currentYear = recordMonthYear[0];

    // Get Month-Year of selected month (eg. 08-2024) Used for getting array of All days of month
    let monthYear = `${currentMonth}-${currentYear}`;

    /* ******************** Calculate Summary ************************* */

    let absentCount =
      records ?
        Object.values(records).filter(
          (rec) => rec.message && rec.message === "Absent",
        ).length
      : 0;

    const summaryData = Object.values(records).reduce(
      (prev, curr) => {
        if (Object.keys(curr).length === 0) {
          return prev;
        }

        // Add totalLateTime totalOverTime totalWorkingHours
        prev.totalLateTime =
          curr.totalLateTime ?
            prev.totalLateTime + curr.totalLateTime
          : prev.totalLateTime;

        prev.totalOverTime =
          curr.overTime ?
            prev.totalOverTime + curr.overTime
          : prev.totalOverTime;
        prev.totalWorkingHours =
          curr.totalHours ?
            prev.totalWorkingHours + curr.totalHours
          : prev.totalWorkingHours;

        return prev;
      },
      {
        totalLateTime: 0.0,
        totalOverTime: 0.0,
        totalWorkingHours: 0.0,
      },
    );

    let parsedSummaryData = {
      totalLateTime: summaryData.totalLateTime,
      totalOverTime: summaryData.totalOverTime,
      totalWorkingHours: summaryData.totalWorkingHours,
    };

    console.log("selectedReport", {
      attendanceRecords: records,
      isNightOffice: true,
      userData: selectedUser,
      monthYear: monthYear,
      absentDaysCount: absentCount,
      summaryData: parsedSummaryData,
    });

    // console.log("Received Records:", logs);
    setSelectedReport({
      attendanceRecords: records,
      userData: selectedUser,
      monthYear: monthYear,
      isNightOffice: true,
      absentDaysCount: absentCount,
      summaryData: parsedSummaryData,
    });

    // setMonthlyRecords(records);
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      if (!deviceId) return;
      if (showModal) {
        getUsersDataByDeviceId(deviceId);
      }
      GetDeviceById(deviceId);
    }, 50);
    return () => clearTimeout(timeout);
  }, [deviceId, showModal]);

  return (
    <>
      {selectedDevice && (
        <button
          onClick={(e) => {
            setShowModal(true);
          }}
          className="btn btn-info ms-1"
        >
          Generate Attendance
        </button>
      )}
      <Modal
        contentClassName="bg-transparent"
        show={showModal}
        onHide={() => {
          setShowModal(false);
        }}
        size="lg"
      >
        <Modal.Body className="bg-white custom-border-radius px-4">
          <div>
            {(loading || !usersList) && <Spinner />}
            <h3 className="mb-3">Generate Attendance Report</h3>

            {/* User Navigation Controls */}
            {usersList.length > 1 && selectedUser && (
              <div className="d-flex align-items-center justify-content-between mb-3 p-2 bg-light rounded">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={goToPrevUser}
                  disabled={currentUserIndex <= 0 || loading}
                >
                  <i className="bi bi-arrow-left"></i> Previous
                </button>

                <div className="text-center">
                  <span className="fw-bold">{selectedUser.name || "User"}</span>
                  <div className="small text-muted">
                    {currentUserIndex + 1} of {usersList.length}
                  </div>
                </div>

                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={goToNextUser}
                  disabled={currentUserIndex >= usersList.length - 1 || loading}
                >
                  Next <i className="bi bi-arrow-right"></i>
                </button>
              </div>
            )}

            <div>
              <label className="form-label">Select Month</label>
              <input
                className="form-control"
                type="month"
                value={selectedMonth || ""}
                onChange={handleSelectMonth}
              />
            </div>
            <div className="mt-1">
              <span className="fw-bold">Current Time Zone :</span>{" "}
              {selectedDevice?.timezone} <br />
              <span className="fw-bold">Current Time :</span>{" "}
              {getCurTimeOfDeviceTimeZone(selectedDevice)}
            </div>
            <div className="d-flex flex-nowrap justify-content-end align-items-center gap-1 mt-3">
              <button
                onClick={(e) => {
                  setShowModal(false);
                }}
                className="btn btn-sm btn-secondary text-nowrap"
              >
                Close
              </button>
              <button
                onClick={GenerateReport}
                disabled={!selectedUser || !selectedMonth || loading}
                type="button"
                className="btn btn-sm btn-info text-nowrap"
              >
                Get Records
              </button>
              {selectedReport &&
                Object.keys(selectedReport?.attendanceRecords).length > 0 && (
                  <AttReportBtn
                    selectedReport={selectedReport}
                    btnClasses={"btn btn-primary btn-sm text-nowrap"}
                  />
                )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
